import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { FileWarning, Filter } from 'lucide-react'

import type { DashboardGroupBy } from '@/hooks/use-warehouse-dashboard-query'
import { ListPageHeader } from '@/components/common/ListPageHeader'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { DefectsTable } from '@/components/warehouse/defects-table'
import { InventoryPeriodFilter } from '@/components/warehouse/inventory-period-filter'
import { useAuth } from '@/hooks/use-auth'
import { useDefects } from '@/hooks/use-warehouse-dashboard-query'
import { getPresetRange } from '@/lib/date-presets'
import { m } from '@/lib/paraglide/messages'

const PERMISSION = 'warehouse_sales.read'

interface DefectsSearch {
    date_from?: string
    date_to?: string
    group_by?: DashboardGroupBy
}

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/warehouse-sales/defects',
)({
    validateSearch: (search: Record<string, unknown>): DefectsSearch => ({
        date_from:
            typeof search.date_from === 'string' ? search.date_from : undefined,
        date_to:
            typeof search.date_to === 'string' ? search.date_to : undefined,
        group_by:
            search.group_by === 'day' ||
            search.group_by === 'week' ||
            search.group_by === 'month' ||
            search.group_by === 'year'
                ? search.group_by
                : undefined,
    }),
    component: WarehouseSalesDefectsPage,
})

function WarehouseSalesDefectsPage() {
    const { hasPermission, arePermissionsLoaded } = useAuth()

    if (arePermissionsLoaded && !hasPermission(PERMISSION)) {
        return <ForbiddenState />
    }

    return <DefectsContent />
}

function DefectsContent() {
    const search = Route.useSearch()
    const navigate = Route.useNavigate()

    const fallback = getPresetRange('7d')
    const dateFrom = search.date_from ?? fallback.from
    const dateTo = search.date_to ?? fallback.to
    const groupBy: DashboardGroupBy = search.group_by ?? 'day'

    const [draftFrom, setDraftFrom] = useState(dateFrom)
    const [draftTo, setDraftTo] = useState(dateTo)
    const [filterDialogOpen, setFilterDialogOpen] = useState(false)

    const setSearch = (next: Partial<DefectsSearch>) => {
        void navigate({
            search: (prev) => ({ ...prev, ...next }),
            replace: true,
        })
    }

    const params = {
        date_from: dateFrom,
        date_to: dateTo,
        group_by: groupBy,
    }

    const { data: items = [], isLoading } = useDefects(params)
    const renderFilters = () => (
        <>
            <InventoryPeriodFilter
                dateFrom={draftFrom}
                dateTo={draftTo}
                groupBy={groupBy}
                onDateFromChange={(v) => {
                    setDraftFrom(v)
                    setSearch({ date_from: v })
                }}
                onDateToChange={(v) => {
                    setDraftTo(v)
                    setSearch({ date_to: v })
                }}
                onGroupByChange={(v) => setSearch({ group_by: v })}
            />
        </>
    )

    return (
        <div className="space-y-4 page-pad md:space-y-6">
            <ListPageHeader
                icon={<FileWarning className="size-5 shrink-0" />}
                title={m.warehouse_defects_page_title()}
                utilityActions={
                    <Button
                        variant="outline"
                        size="icon"
                        className="md:hidden"
                        onClick={() => setFilterDialogOpen(true)}
                    >
                        <Filter className="size-4" />
                    </Button>
                }
            />
            <p className="text-sm text-muted-foreground">
                {m.warehouse_defects_page_subtitle()}
            </p>

            <Card className="hidden md:block">
                <CardContent className="pt-6 space-y-4">
                    {renderFilters()}
                </CardContent>
            </Card>

            <Dialog open={filterDialogOpen} onOpenChange={setFilterDialogOpen}>
                <DialogContent className="max-w-none w-screen h-[100svh] grid-rows-[auto_minmax(0,1fr)_auto] overflow-hidden rounded-none p-4 sm:h-auto sm:w-full sm:max-w-lg sm:rounded-xl sm:p-6">
                    <DialogHeader>
                        <DialogTitle>
                            {m.warehouse_defects_page_title()}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="grid content-start gap-3 overflow-y-auto py-2">
                        {renderFilters()}
                    </div>
                    <DialogFooter className="flex-row gap-2 pt-2 sm:justify-end">
                        <Button
                            className="h-11 flex-1"
                            onClick={() => setFilterDialogOpen(false)}
                        >
                            {m.common_filter_apply()}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <DefectsTable items={items} isLoading={isLoading} />

            <Card>
                <CardContent className="py-4 flex flex-wrap items-center justify-between gap-2">
                    <p className="text-sm text-muted-foreground">
                        {m.warehouse_defects_return_to_sorting_hint()}
                    </p>
                    <Button
                        variant="outline"
                        size="sm"
                        disabled
                        title={m.warehouse_defects_return_to_sorting_disabled_tooltip()}
                    >
                        {m.warehouse_defects_return_to_sorting()}
                    </Button>
                </CardContent>
            </Card>
        </div>
    )
}

function ForbiddenState() {
    return (
        <Card>
            <CardContent className="py-12 text-center space-y-2">
                <h2 className="text-lg font-semibold">
                    {m.warehouse_sales_forbidden_title()}
                </h2>
                <p className="text-sm text-muted-foreground">
                    {m.warehouse_sales_forbidden_desc()}
                </p>
            </CardContent>
        </Card>
    )
}
